'use client'

import { useTagTranslations } from '@/hooks/use-tag-translations'
import type { StoryMetadata } from '@/content/stories'
import { useOfflineStories } from '@/hooks/useOfflineStories'
import {
  IconArrowRight,
  IconCheck,
  IconDownload,
  IconLock
} from '@tabler/icons-react'
import { useLocale } from 'next-intl'
import { Badge } from '@/components/ui/badge'
import { cn } from '@/lib/utils'
import type { ReactNode } from 'react'
import {
  getStoryProgressDisplay,
  progressPercent,
  type ProgressStateLike
} from '@/lib/content-progress-core'
import { Link } from '@/i18n/routing'
import Image from 'next/image'
import { MONETIZATION_ENABLED } from '@/monetization-switch'

interface StoryCardProps {
  story: StoryMetadata
  readStoryText: string
  href?: string
  className?: string
  learningState?: (ProgressStateLike & { familiarity?: number | null }) | null
  recommendationReasons?: string[]
  familiarityPercent?: number | null
  familiarityLabel?: string
  variant?: 'default' | 'compact'
}

export function StoryCard({
  story,
  readStoryText,
  href,
  className,
  learningState,
  familiarityPercent = null,
  familiarityLabel = 'content known',
  variant = 'default'
}: StoryCardProps) {
  const { translateLevel, translateStoryTopics } = useTagTranslations()
  const locale = useLocale()
  const { isStorySaved } = useOfflineStories(locale)
  const storyHref = href || `/read/${story.slug}`
  const isSaved = isStorySaved(story.slug)
  const chapterLabel =
    story.chapterNumber && story.chapterCount
      ? `Chapter ${story.chapterNumber}/${story.chapterCount}`
      : story.chapterNumber
        ? `Chapter ${story.chapterNumber}`
        : null
  const subtitle = story.title_en
  const progressDisplay = getStoryProgressDisplay(learningState)
  const translatedTopics = translateStoryTopics(story.topics)
  const compact = variant === 'compact'
  const familiarityFromState =
    typeof learningState?.familiarity === 'number'
      ? Math.round(learningState.familiarity * 100)
      : null
  const readProgressPercent =
    progressDisplay?.kind === 'read'
      ? 100
      : progressPercent(learningState?.progress)
  const cardProgressPercent =
    typeof familiarityPercent === 'number'
      ? Math.round(familiarityPercent)
      : (familiarityFromState ?? readProgressPercent)
  const showVisualProgress =
    typeof cardProgressPercent === 'number' &&
    cardProgressPercent > 0 &&
    cardProgressPercent < 100

  return (
    <article
      className={cn(
        'group bg-card focus-within:ring-ring/35 relative flex min-w-0 overflow-hidden rounded-lg border transition focus-within:ring-2',
        'hover:border-foreground/25 hover:bg-muted/20',
        compact ? 'h-[21.5rem]' : 'h-full',
        className
      )}
    >
      <Link
        href={storyHref}
        aria-label={`${readStoryText}: ${story.simplifiedTitle}`}
        className={cn(
          'flex h-full min-w-0 flex-1 flex-col outline-none',
          compact ? 'min-h-44' : 'min-h-56'
        )}
      >
        {story.coverImage && (
          <div
            className={cn(
              'bg-muted relative w-full overflow-hidden',
              'aspect-[4/3]'
            )}
          >
            <Image
              src={story.coverImage.src}
              alt={story.coverImage.alt}
              fill
              sizes={
                compact
                  ? '(min-width: 1024px) 260px, (min-width: 640px) 280px, 76vw'
                  : '(min-width: 1280px) 360px, (min-width: 640px) 50vw, 100vw'
              }
              className="object-cover transition-transform duration-500 group-hover:scale-[1.015]"
            />
            {(chapterLabel || progressDisplay || showVisualProgress) && (
              <div className="absolute top-2 right-2 flex max-w-[70%] flex-wrap justify-end gap-1.5">
                {progressDisplay?.kind === 'read' && (
                  <StoryOverlayPill ariaLabel="Read">
                    <IconCheck className="h-3 w-3" />
                    Read
                  </StoryOverlayPill>
                )}
                {progressDisplay?.kind === 'started' &&
                  typeof readProgressPercent === 'number' && (
                    <StoryOverlayPill
                      ariaLabel={`${readProgressPercent}% read`}
                    >
                      {readProgressPercent}% read
                    </StoryOverlayPill>
                  )}
                {showVisualProgress && !progressDisplay && (
                  <StoryOverlayPill
                    ariaLabel={`${cardProgressPercent}% ${familiarityLabel}`}
                  >
                    {cardProgressPercent}% {familiarityLabel}
                  </StoryOverlayPill>
                )}
                {chapterLabel && (
                  <StoryOverlayPill ariaLabel={chapterLabel}>
                    {chapterLabel}
                  </StoryOverlayPill>
                )}
              </div>
            )}
          </div>
        )}

        <div
          className={cn(
            'flex min-w-0 flex-1 flex-col',
            compact ? 'p-3.5' : 'p-4 sm:p-5'
          )}
        >
          <div className="flex min-w-0 items-center justify-between gap-3">
            <div className="flex min-w-0 flex-wrap items-center gap-1.5">
              <p className="text-muted-foreground text-xs font-semibold tracking-wide uppercase">
                {translateLevel(story.level)}
              </p>
              {MONETIZATION_ENABLED && story.isPremiumChapter && (
                <Badge
                  variant="outline"
                  className="gap-1 border-amber-500/40 bg-amber-500/10 text-[10px] text-amber-700 dark:text-amber-300"
                >
                  <IconLock className="h-3 w-3" />
                  Pro
                </Badge>
              )}
            </div>
            {chapterLabel && !story.coverImage && (
              <p className="text-muted-foreground shrink-0 text-xs">
                {chapterLabel}
              </p>
            )}
          </div>

          {isSaved && (
            <div className="mt-2 flex flex-wrap gap-1.5">
              <Badge variant="outline" className="gap-1 text-[10px]">
                <IconDownload className="h-3 w-3" />
                Offline
              </Badge>
            </div>
          )}

          <div className={cn('min-w-0 space-y-1.5', compact ? 'mt-3' : 'mt-5')}>
            <h3
              className={cn(
                'truncate leading-tight font-semibold tracking-tight',
                compact ? 'text-lg' : 'text-2xl'
              )}
            >
              {story.simplifiedTitle}
            </h3>
            <p
              className={cn(
                'text-muted-foreground line-clamp-2 leading-6',
                compact ? 'text-xs' : 'text-sm'
              )}
            >
              {subtitle}
            </p>
          </div>

          {translatedTopics.length > 0 && (
            <div
              className={cn(
                'flex flex-wrap gap-1.5',
                compact ? 'mt-3' : 'mt-4'
              )}
            >
              {translatedTopics.slice(0, compact ? 2 : 3).map((topic) => (
                <Badge key={topic} variant="neutral" className="text-[10px]">
                  {topic}
                </Badge>
              ))}
            </div>
          )}

          <span
            className={cn(
              'mt-auto flex w-full items-center justify-between gap-3 font-semibold',
              compact ? 'pt-4 text-xs' : 'pt-5 text-sm'
            )}
          >
            <span>{readStoryText}</span>
            <span className="bg-foreground text-background inline-flex h-7 w-7 items-center justify-center rounded-full transition group-hover:translate-x-0.5">
              <IconArrowRight className="h-4 w-4" />
            </span>
          </span>
        </div>
      </Link>
    </article>
  )
}

function StoryOverlayPill({
  children,
  ariaLabel
}: {
  children: ReactNode
  ariaLabel: string
}) {
  return (
    <span
      className="bg-background/82 text-foreground/85 ring-border/45 inline-flex h-6 max-w-full items-center gap-1 rounded-full px-2 text-[10px] font-semibold shadow-sm ring-1 backdrop-blur-sm"
      aria-label={ariaLabel}
      title={ariaLabel}
    >
      {children}
    </span>
  )
}
